import React, { Component } from 'react';
import {
  Button, Card, CardBody, CardTitle, FormGroup, Input,
} from 'reactstrap';
import { actions, Control, Errors } from 'react-redux-form/immutable';
import PropTypes from 'prop-types';
import { fromJS, Map } from 'immutable';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import classnames from 'classnames';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { AddIcon } from '../icon/AddIcon';
import { RemoveIcon } from '../table/RemoveIcon';
import { fieldToNumber, fieldTrimmed } from './formUtilities';
import { Column } from '../grid/Column';
import { Loading } from '../loadbar/Loading';
import { ResultGrid } from '../grid/ResultGrid';
import { RawHtml } from './RawHtml';
import { SingleResultGrid } from '../grid/SingleResultGrid';
import {
  minColumns,
  refSort,
  refVisibleColumns,
} from '../../redux/EditReduxConstant';

const resolvePublish = ({ value }) => {
  if (value == null) {
    return null;
  }
  return value ? 'Y' : 'N';
};

const defaultMap = fromJS({});

class SingularAssociatedReferenceControlUnwrapped extends Component {
  constructor(props) {
    super(props);
    this.searchReferences = this.searchReferences.bind(this);
    this.addReference = this.addReference.bind(this);
    this.handleToggleColumns = this.handleToggleColumns.bind(this);
    this.resolveRemove = this.resolveRemove.bind(this);
    this.toggleSearch = this.toggleSearch.bind(this);
    this.renderAddButton = this.renderAddButton.bind(this);
    this.state = {
      references: Map(),
      searchVisible: false,
      searchInput: '',
    };
  }

  componentDidMount() {
    const { reference } = this.props;
    this.setState((state) => {
      const referenceId = reference.get('id');
      const references = referenceId ? state.references.set(`${referenceId}`, reference) : state.references;
      return {
        references,
      };
    });
  }

  searchReferences() {
    const { searchReferences } = this.props;
    const { searchInput } = this.state;
    searchReferences({
      queryParameters: {
        op: 'or',
        citInclude: fieldTrimmed(searchInput),
        id: fieldToNumber(searchInput),
        authorInclude: fieldTrimmed(searchInput),
      },
    });
  }

  addReference(selectedRef) {
    const { reference, change, model } = this.props;
    const id = selectedRef ? selectedRef.get('id') : '';
    this.setState((state) => {
      const references = state.references.filter(existing => existing.get('id') === reference.get('id'));
      return {
        references: selectedRef ? references.set(`${id}`, selectedRef) : references,
      };
    }, () => {
      change(`${model}.referenceId`, `${id}`);
    });
  }

  resolveRemove({ row }) {
    if (row.get('id') == null) {
      return null;
    }
    return (<RemoveIcon handleClick={() => this.addReference()} />);
  }

  handleToggleColumns({ visibleColumns, sort }) {
    const {
      visibleColumns: preVisibleColumns, sort: prevSort, sortData, toggleColumns,
    } = this.props;
    let changed = false;
    if (!visibleColumns.sort().equals(preVisibleColumns.sort())) {
      toggleColumns(visibleColumns);
      changed = true;
    }
    if (!sort.equals(prevSort)) {
      sortData(sort);
      changed = true;
    }
    return changed;
  }

  toggleSearch() {
    this.setState(({ searchVisible: prev }) => ({ searchVisible: !prev }));
  }

  renderAddButton({ row }) {
    const { referenceId } = this.props;
    if (`${row.get('id')}` !== referenceId) {
      return (<AddIcon handleClick={() => this.addReference(row)} />);
    }
    return (<div />);
  }

  render() {
    const {
      referenceSearching, referenceSearchResults, valid, sort, referenceId, visibleColumns,
    } = this.props;
    const { references, searchVisible } = this.state;
    return (
      <Card className="haz-assoc-ref">
        <CardBody>
          <CardTitle tag="h2">Reference</CardTitle>
          <SingleResultGrid data={references.get(referenceId) ? references.get(referenceId) : defaultMap}>
            <Column id="remove" cell={this.resolveRemove} width={50}><RemoveIcon /></Column>
            <Column id="id" width={100}>ID</Column>
            <Column id="publish" cell={resolvePublish} width={85}>Publish</Column>
            <Column id="author" width={150}>Author</Column>
            <Column id="year" width={120}>Year</Column>
            <Column id="citation" width={200} cell={({ value }) => (<RawHtml>{value}</RawHtml>)}>Citation</Column>
          </SingleResultGrid>
          <Control
            type="hidden"
            model=".referenceId"
            mapProps={{
              tabIndex: '-1',
              className: ({ fieldValue }) => classnames({ 'form-control': true, 'sr-only': true, 'is-invalid': !fieldValue.valid }),
            }}
          />

          <Errors
            model=".referenceId"
            component="div"
            wrapper={({ children }) => <div className={classnames({ 'invalid-feedback': true, 'haz-associated-table-error': !valid })}>{children}</div>}
          />

          {searchVisible ? (
            <Card>
              <CardBody>
                <CardTitle tag="h2" className="haz-close-title">
                  <span>Search</span>
                  <button type="button" className="close" aria-label="Close" onClick={this.toggleSearch}>x</button>
                </CardTitle>
                <div className="haz-assoc-search-container">
                  <div className="form-inline">
                    <FormGroup className="mb-2 mr-sm-2 mb-sm-0">
                      <Input disabled={referenceSearching} onChange={(e) => { this.setState({ searchInput: e.target.value }); }} type="text" />
                    </FormGroup>
                    <Button disabled={referenceSearching} onClick={this.searchReferences}>Search</Button>
                  </div>

                  <div className="haz-assoc-search">
                    {referenceSearching ? (<Loading />) : (
                      <ResultGrid
                        data={referenceSearchResults}
                        sort={sort}
                        handleColumnsToggle={this.handleToggleColumns}
                        columnChooser
                        visibleColumns={visibleColumns}
                        defaultColumns={refVisibleColumns}
                        defaultSort={refSort}
                        minColumns={minColumns}
                      >
                        <Column id="add" width={50} alwaysVisible cell={this.renderAddButton}><AddIcon /></Column>
                        <Column id="id" width={100} sortable>ID</Column>
                        <Column id="publish" cell={resolvePublish} width={120} sortable>Publish</Column>
                        <Column id="author" width={200} sortable>Author</Column>
                        <Column id="year" width={150} sortable>Year</Column>
                        <Column id="citation" width={250} sortable cell={({ value }) => (<RawHtml>{value}</RawHtml>)}>Citation</Column>
                      </ResultGrid>
                    )}
                  </div>
                </div>
              </CardBody>
            </Card>
          ) : (<Button onClick={this.toggleSearch}>Search</Button>)}


        </CardBody>
      </Card>
    );
  }
}


SingularAssociatedReferenceControlUnwrapped.propTypes = {
  model: PropTypes.string.isRequired,
  reference: ImmutablePropTypes.map.isRequired,
  searchReferences: PropTypes.func.isRequired,
  referenceSearching: PropTypes.bool.isRequired,
  referenceSearchResults: ImmutablePropTypes.list.isRequired,
  change: PropTypes.func.isRequired,
  referenceId: PropTypes.string.isRequired,
  valid: PropTypes.bool.isRequired,
  sortData: PropTypes.func.isRequired,
  sort: ImmutablePropTypes.list.isRequired,
  visibleColumns: ImmutablePropTypes.list.isRequired,
  toggleColumns: PropTypes.func.isRequired,
};


const mapStateToProps = (state, { model }) => ({
  referenceId: state.getIn([model, 'referenceId']),
  valid: state.get('forms')[model].referenceId.valid,
});

const mapDispatchToProps = dispatch => bindActionCreators({
  change: actions.change,

}, dispatch);

export const SingularAssociatedReferenceControl = connect(mapStateToProps, mapDispatchToProps)(SingularAssociatedReferenceControlUnwrapped);
